import { useState, useCallback, useEffect } from 'react';
// api
import { losamApiService } from 'src/api/vuontrong-api';
import { homeAPI } from 'src/api/home-api';
import { caySamApiService, AddCaySamData } from 'src/api/caysam-api';
// types
import { LoSam, LoSamApi } from 'src/types/losam';
import { ApiResponse,OptionModel } from 'src/types/api-response';
import { LoSamChiTiet } from 'src/types/losamchitiet';
import { LoSamCamera } from 'src/types/camera';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import systemApi from 'src/api/system-api';
import { CaySamUser } from 'src/types/caysamuser';
import { useAuthContext } from 'src/auth/context/jwt';

// ----------------------------------------------------------------------

export function useLoSam() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaiTuoiOptions, setLoaiTuoiOptions] = useState<OptionModel[]>([]);
  const [loaiCameraOptions, setLoaiCameraOptions] = useState<OptionModel[]>([]);
  const [diemSucKhoeOptions, setDiemSucKhoeOptions] = useState<OptionModel[]>([]);
  const [GetCaySamByUser, setGetCaySamByUser] = useState<CaySamUser[]>([]);
  const { isAdmin } = useAuthContext();
  // Fetch options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch loai tuoi options
        const tuoiResponse = await homeAPI.OptionLoSamLoaiTuoi();
        if (tuoiResponse.messCode === 1) {
          const tuoiItems = (tuoiResponse.items || []) as unknown as any[];
          const tuoiFlattened = Array.isArray(tuoiItems[0]) ? (tuoiItems as any[]).flat() : tuoiItems;
          setLoaiTuoiOptions(tuoiFlattened as OptionModel[]);
        }

        // Fetch loai camera options
        const cameraResponse = await homeAPI.OptionLoSamLoaiCamera();
        if (cameraResponse.messCode === 1) {
          const cameraItems = (cameraResponse.items || []) as unknown as any[];
          const cameraFlattened = Array.isArray(cameraItems[0]) ? (cameraItems as any[]).flat() : cameraItems;
          setLoaiCameraOptions(cameraFlattened as OptionModel[]);
        }

        // Fetch Điểm sức khỏe options (OptionModel)
        const skResponse = await homeAPI.OptionCaySamDiemSucKhoe();
        if (skResponse.messCode === 1) {
          const skItems = (skResponse.items || []) as unknown as any[];
          const skFlattened = Array.isArray(skItems[0]) ? (skItems as any[]).flat() : skItems;
          setDiemSucKhoeOptions(skFlattened as OptionModel[]);
        }
        if (!isAdmin) {
          const CaySamOfUser = await systemApi.GetCaySamByUser();
          if (CaySamOfUser.messCode === 1) {
            setGetCaySamByUser(CaySamOfUser.items as CaySamUser[]);
          }
        }

      } catch (err) {
        console.error('Error fetching options:', err);
      }
    };

    fetchOptions();
  }, [isAdmin]); 

  // Add LoSam
  const addLoSam = useCallback(
    async (data: LoSamApi, file?: File): Promise<ApiResponse<LoSam>> => {
      setLoading(true);
      setError(null);
      try {
        if (!data.vuonTrong_ID) {
          throw new Error('Thiếu vuonTrong_ID');
        }

        const loSamData: LoSamApi = {
          loSam_ID: 0,
          vuonTrong_ID: data.vuonTrong_ID,
          tenLo: data.tenLo || '',
          maLo: data.maLo || '',
          ngay: data.ngay,
          dienTich: data.dienTich || 0,
          loai: data.loai || '',
          ghiChu: data.ghiChu || '',
          soHang: data.soHang || 0,
          soCot: data.soCot || 0,
          hinhAnh: data.hinhAnh || '',
          trangThai: data.trangThai ?? 1,
          sensorIds: data.sensorIds || []
        };

        const chiTietLoSam: LoSamChiTiet[] = data.loSamChiTiets || [];
        const cameras: LoSamCamera[] = data.loSamCameras || [];
        const response = await losamApiService.addLoSam(loSamData, chiTietLoSam, cameras, file);
        return response;
      } catch (err: any) {
        setError(err?.message || 'Không thể tạo lô sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Edit LoSam
  const editLoSam = useCallback(
    async (data: LoSamApi, file?: File): Promise<ApiResponse<LoSam>> => {
      setLoading(true);
      setError(null);
      try {
        if (!data.loSam_ID) {
          throw new Error('Thiếu loSam_ID');
        }
        const loSamData: LoSamApi = {
          loSam_ID: data.loSam_ID,
          vuonTrong_ID: data.vuonTrong_ID || 0,
          tenLo: data.tenLo || '',
          maLo: data.maLo || '',
          ngay: data.ngay,
          dienTich: data.dienTich || 0,
          loai: data.loai || '',
          ghiChu: data.ghiChu || '',
          soHang: data.soHang || 0,
          soCot: data.soCot || 0,
          hinhAnh: data.hinhAnh || '',
          trangThai: data.trangThai ?? 1,
          sensorIds: data.sensorIds || []
        };
        const chiTietLoSam: LoSamChiTiet[] = data.loSamChiTiets || [];
        const cameras: LoSamCamera[] = data.loSamCameras || [];
        const response = await losamApiService.editLoSam(loSamData, chiTietLoSam, cameras, file);
        return response;
      } catch (err: any) {
        setError(err?.message || 'Không thể cập nhật lô sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Upload Image
  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      setLoading(true);
      setError(null);
      try {

        return URL.createObjectURL(file);
      } catch (err: any) {
        setError(err?.message || 'Không thể tải lên hình ảnh');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // List LoSam
  const listLoSamFlat = useCallback(
    async (params?: {
      skip?: number;
      take?: number;
      Status?: number;
      rowCount?: number;
      searchBy?: string[];
      orderBy?: string[];
    }): Promise<LoSam[]> => {
      setLoading(true);
      setError(null);
      try {
        const rsp = await losamApiService.listLoSam(params);
        const items = (rsp.items || []) as unknown as any[];
        const flattened = Array.isArray(items[0]) ? (items as any[]).flat() : items;
        return flattened as LoSam[];
      } catch (err: any) {
        setError(err?.message || 'Không thể tải danh sách lô sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );


  // Get LoSam by ID
  const getLoSamById = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const rsp = await losamApiService.getLoSam(id);
      return rsp;
    } catch (err: any) {
      setError(err?.message || 'Không thể tải thông tin lô sâm');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete LoSam
  const deleteLoSam = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const rsp = await losamApiService.deleteLoSam(id);
      return rsp;
    } catch (err: any) {
      setError(err?.message || 'Không thể xóa lô sâm');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // List lots by VuonTrong
  const listLotsByVuonTrong = useCallback(async (vuonTrongId: number) =>
    listLoSamFlat({ searchBy: [`VuonTrong_ID Equals '${vuonTrongId}'`],Status: 1 })
    , [listLoSamFlat]);

  // Parse vi tri trong lo
  const parseViTriTrongLo = useCallback((viTriTrongLo: string) => {
    const match = viTriTrongLo?.match?.(/([A-Z])(\d+)/);
    if (match) {
      const x = match[1].charCodeAt(0) - 65; // A=0, B=1, ...
      const y = parseInt(match[2], 10) - 1; // 1-based -> 0-based
      return { x, y };
    }
    return null;
  }, []);

  return {
    loading,
    error,
    loaiTuoiOptions,
    loaiCameraOptions,
    diemSucKhoeOptions,
    GetCaySamByUser,
    addLoSam,
    editLoSam,
    uploadImage,
    listLoSamFlat,
    getLoSamById,
    deleteLoSam,
    listLotsByVuonTrong,
    parseViTriTrongLo,
  };
}

// Hook cho cây sâm
export function useCaySam() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add cây sâm mới
  const addCaySam = useCallback(
    async (data: AddCaySamData, files: File[] = []): Promise<ApiResponse<CaySam>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await caySamApiService.addCaySam(data, files);
        return response;
      } catch (err: any) {
        setError(err?.message || 'Không thể thêm cây sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Update cây sâm
  const updateCaySam = useCallback(
    async (caySamId: string, data: Partial<CaySam>): Promise<ApiResponse<CaySam>> => {
      setLoading(true);
      setError(null);
      try {
        // TODO: Implement update API when available
        throw new Error('API cập nhật cây sâm chưa được implement');
      } catch (err: any) {
        setError(err?.message || 'Không thể cập nhật cây sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Delete cây sâm
  const deleteCaySam = useCallback(
    async (caySamId: string): Promise<ApiResponse<any>> => {
      setLoading(true);
      setError(null);
      try {
        // TODO: Implement delete API when available
        throw new Error('API xóa cây sâm chưa được implement');
      } catch (err: any) {
        setError(err?.message || 'Không thể xóa cây sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Add/Update nhật ký cho cây sâm đơn lẻ
  const addNhatKyByCaySamId = useCallback(
    async (
      data: {
        CaySamId: string;
        NgayGhi: string;
        SoLa: number;
        DiemSucKhoe: number;
        TinhTrang: number;
        IsThongBao: boolean;
        trongLuong: number;
        GhiChu: string;
      },
      files: File[] = []
    ): Promise<ApiResponse<CaySamNhatKy>> => {
      setLoading(true);
      setError(null);
      try {
        const result = await caySamApiService.addNhatKyByCaySamId(data, files);
        return result;
      } catch (err: any) {
        setError(err?.message || 'Không thể thêm/cập nhật nhật ký cây sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );
  // Add/Update nhật ký cho cây sâm đơn lẻ
  const editNhatKyByCaySamId = useCallback(
    async (
      data: {
        CaySamId: string;
        NgayGhi: string;
        SoLa: number;
        DiemSucKhoe: number;
        TinhTrang: number;
        IsThongBao: boolean;
        trongLuong: number;
        GhiChu : string;
      },
      files: File[] = []
    ): Promise<ApiResponse<CaySamNhatKy>> => {
      setLoading(true); 
      setError(null);
      try {
        const result = await caySamApiService.editNhatKyByCaySamId(data, files);
        return result;
      } catch (err: any) {
        setError(err?.message || 'Không thể thêm/cập nhật nhật ký cây sâm');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    addCaySam,
    updateCaySam,
    deleteCaySam,
    addNhatKyByCaySamId,
    editNhatKyByCaySamId
  };
}

// Export convenience hooks for backward compatibility
export const useLoSamOptions = () => {
  const { loaiTuoiOptions, loaiCameraOptions, loading, error } = useLoSam();
  return { loaiTuoiOptions, loaiCameraOptions, loading, error };
};

export default useLoSam;
 